// ✅ Trekkin.app — StatsPanelIcons.jsx v1.2.1 (AUTOFORMAT FINAL)
// 🔥 Converte automaticamente números crus → texto formatado (hh:mm:ss, km/h, km, m, pts)
// 🔥 Compatível com Nav / Realtime / LiveShare / Viewer
// 🔥 100% retrocompatível (aceita também strings já formatadas)

import { useTranslate } from "../i18n/useTranslate";

export default function StatsPanelIcons({
  base = import.meta.env?.BASE_URL || "/",
  variant = "navRecording",
  forceVisible = false,

  // Linha 1 — segundos (number) ou string "hh:mm:ss"
  totalTime = 0,
  stoppedTime = 0,
  movingTime = 0,

  // Linha 2 — valores crus (number) ou strings
  speed = 0,    // km/h
  dPlus = 0,    // metros
  distance = 0, // metros

  // Linha 3 — valores crus
  altitude = 0, // metros
  coords = null,
  points = 0,
}) {
  const translate = useTranslate();
  // ----------------------------
  // Helpers
  // ----------------------------
  const formatTime = (sec) => {
    if (typeof sec === "string") return sec;
    let v = Number(sec || 0);
    if (!Number.isFinite(v)) v = 0;
    v = Math.max(0, Math.floor(v));

    const h = String(Math.floor(v / 3600)).padStart(2, "0");
    const m = String(Math.floor((v % 3600) / 60)).padStart(2, "0");
    const s = String(v % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const formatDistance = (m) => {
    if (typeof m === "string") return m;
    let v = Number(m || 0);
    if (!Number.isFinite(v)) v = 0;
    return `${(v / 1000).toFixed(2)} km`;
  };

  const formatSpeed = (v) => {
    if (typeof v === "string") return v;
    let n = Number(v || 0);
    if (!Number.isFinite(n)) n = 0;
    return `${n.toFixed(1)} km/h`;
  };

  const formatMeters = (m) => {
    if (typeof m === "string") return m;
    let v = Number(m || 0);
    if (!Number.isFinite(v)) v = 0;
    return `${Math.round(v)} m`;
  };

  const formatPoints = (p) => {
    if (typeof p === "string") return p;
    let v = Number(p || 0);
    if (!Number.isFinite(v)) v = 0;
    return `${v} pts`;
  };

  const formatCoords = (c) => {
    if (!c) return { line1: "—", line2: "—" };

    if (typeof c === "string") {
      const raw = c.trim();
      if (!raw) return { line1: "—", line2: "—" };
      if (raw.includes("/")) {
        const [a, b] = raw.split("/");
        return {
          line1: String(a || "—").trim() || "—",
          line2: String(b || "—").trim() || "—",
        };
      }
      if (raw.includes(",")) {
        const [a, b] = raw.split(",");
        return {
          line1: String(a || "—").trim() || "—",
          line2: String(b || "—").trim() || "—",
        };
      }
      return { line1: raw, line2: "" };
    }

    if (c.lat != null && c.lon != null) {
      const lat = Number(c.lat);
      const lon = Number(c.lon);
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        return { line1: lat.toFixed(5), line2: lon.toFixed(5) };
      }
    }
    return { line1: "—", line2: "—" };
  };

  const toRawNumber = (v) => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string") {
      const n = Number(String(v).replace(",", ".").replace(/[^\d.\-]/g, ""));
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const parseTimeToSec = (v) => {
    if (typeof v === "number" && Number.isFinite(v)) return Math.max(0, v);
    if (typeof v !== "string") return 0;
    const parts = v.trim().split(":").map((x) => Number(x));
    if (parts.length !== 3 || parts.some((x) => !Number.isFinite(x))) return 0;
    return Math.max(0, parts[0] * 3600 + parts[1] * 60 + parts[2]);
  };

  // ----------------------------
  // Formatted values
  // ----------------------------
  const row1 = [
    { icon: "time.svg", value: formatTime(totalTime) },
    { icon: "stopped.svg", value: formatTime(stoppedTime) },
    { icon: "moving.svg", value: formatTime(movingTime) },
  ];

  const row2 = [
    { icon: "speed.svg", value: formatSpeed(speed) },
    { icon: "climb.svg", value: formatMeters(dPlus) },      // usa o ícone D+
    { icon: "distance.svg", value: formatDistance(distance) },
  ];

  const row3 = [
    { icon: "elevation.svg", value: formatMeters(altitude) },
    { icon: "location.svg", value: formatCoords(coords), nowrap: true, isCoords: true },
    { icon: "points.svg", value: formatPoints(points) },
  ];

  const navRecordingTiles = [
    { icon: "time.svg", value: formatTime(totalTime), label: translate("nav.stats.time") },
    { icon: "stopped.svg", value: formatTime(stoppedTime), label: translate("nav.stats.stopped") },
    { icon: "moving.svg", value: formatTime(movingTime), label: translate("nav.stats.moving") },
    { icon: "speed.svg", value: formatSpeed(speed), label: translate("nav.stats.average") },
    { icon: "climb.svg", value: formatMeters(dPlus), label: translate("nav.stats.ascent") },
    { icon: "distance.svg", value: formatDistance(distance), label: translate("nav.stats.distance") },
    { icon: "elevation.svg", value: formatMeters(altitude), label: translate("nav.stats.altitude") },
    { icon: "location.svg", value: formatCoords(coords), isCoords: true, label: translate("nav.stats.location") },
  ];

  const coordsValue = formatCoords(coords);
  const hasCoords =
    (coordsValue?.line1 && coordsValue.line1 !== "—") ||
    (coordsValue?.line2 && coordsValue.line2 !== "—");
  const hasAnyData =
    parseTimeToSec(totalTime) > 0 ||
    parseTimeToSec(stoppedTime) > 0 ||
    parseTimeToSec(movingTime) > 0 ||
    toRawNumber(speed) > 0 ||
    toRawNumber(dPlus) > 0 ||
    toRawNumber(distance) > 0 ||
    toRawNumber(altitude) > 0 ||
    toRawNumber(points) > 0 ||
    hasCoords;

  if (!forceVisible && !hasAnyData) return null;

  // ----------------------------
  // Render
  // ----------------------------
  if (variant === "navRecording") {
    return (
      <div
        className="tk-nav-metrics-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 8,
          marginBottom: 12,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        {navRecordingTiles.map((item, i) => (
          <div
            key={i}
            className="tk-nav-metric-card"
            style={{
              border: "1px solid rgba(0,0,0,0.08)",
              background: "rgba(255,255,255,0.96)",
              borderRadius: 12,
              aspectRatio: "1 / 1",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "6px 4px",
              textAlign: "center",
              boxSizing: "border-box",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <img
              src={`${base}icons/${item.icon}`}
              alt=""
              className="tk-nav-metric-icon"
              style={{ width: 22, height: 22 }}
            />
            <span
              className="tk-nav-metric-label"
              style={{
                fontSize: 9,
                lineHeight: 1.1,
                color: "#4b5563",
                fontWeight: 700,
                maxWidth: "100%",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              {item.label}
            </span>
            {item.isCoords ? (
              <span
                className="tk-nav-metric-value is-coords"
                style={{
                  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                  fontSize: 9,
                  lineHeight: 1.2,
                  whiteSpace: "pre-line",
                  wordBreak: "break-word",
                }}
              >
                {`${item.value?.line1 || "—"}\n${item.value?.line2 || ""}`}
              </span>
            ) : (
              <span
                className="tk-nav-metric-value"
                style={{
                  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                  fontSize: 10,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  maxWidth: "100%",
                }}
              >
                {item.value}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="tk-live-stats"
      style={{
        background: "rgba(255,255,255,0.95)",
        borderRadius: 12,
        padding: "10px",
        border: "1px solid rgba(0,0,0,0.06)",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        marginBottom: 12,
        fontSize: 12,
      }}
    >
      {[row1, row2, row3].map((row, idx) => (
        <div
          key={idx}
          style={{
            display: "flex",
            gap: 6,
            marginBottom: idx < 2 ? 6 : 0,
          }}
        >
          {row.map((item, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                textAlign: "center",
              }}
            >
              <img
                src={`${base}icons/${item.icon}`}
                alt=""
                style={{ width: 22, height: 22 }}
              />

              {item.isCoords ? (
                <span
                  style={{
                    fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                    fontSize: 11,
                    whiteSpace: "pre-line",
                    lineHeight: 1.2,
                  }}
                >
                  {`${item.value?.line1 || "—"}\n${item.value?.line2 || ""}`}
                </span>
              ) : (
                <span
                  style={{
                    fontFamily:
                      "ui-monospace, Menlo, Consolas, monospace",
                    fontSize: 11,
                    whiteSpace: item.nowrap ? "nowrap" : "normal",
                  }}
                >
                  {item.value}
                </span>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
